import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail, setReauthHandler } from "@/lib/api";
import PhoneOtpDialog from "@/components/PhoneOtpDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck } from "lucide-react";

/** "Confirm it's you": shown when the server wants a fresh password or SMS
 *  code before a risky change (adding a password, a new sign-in number…).
 *  Mounted once; the API client waits for it and then retries the change. */
export default function ReauthDialog() {
  const { user } = useAuth();
  const [ask, setAsk] = useState(null);          // server detail: {confirm_with, message}
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const resolver = useRef(null);

  useEffect(() => {
    setReauthHandler((detail) => new Promise((resolve) => {
      resolver.current = resolve;
      setPassword(""); setError("");
      setAsk(detail);
    }));
    return () => setReauthHandler(null);
  }, []);

  const finish = (ok) => {
    setAsk(null);
    resolver.current?.(ok);
    resolver.current = null;
  };

  const confirmPassword = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/reauth", { password });
      finish(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!ask) return null;
  if (ask.confirm_with === "sms_code") {
    return (
      <PhoneOtpDialog open onOpenChange={(o) => !o && finish(false)} title="Confirm it's you" recaptchaId="reauth-recaptcha"
        lockedPhone={(user?.phone || "").replace(/^\+91/, "")}
        onVerified={async (idToken) => { await api.post("/auth/reauth", { id_token: idToken }); finish(true); }} />
    );
  }
  return (
    <Dialog open onOpenChange={(o) => !o && finish(false)}>
      <DialogContent className="max-w-sm" data-testid="reauth-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-teal" /> Confirm it's you</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-stone-600">For your safety, enter your password before making this change.</p>
        <form onSubmit={confirmPassword} className="space-y-3">
          <div>
            <Label htmlFor="reauth-password">Password</Label>
            <Input id="reauth-password" type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 mt-1" data-testid="reauth-password" />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => finish(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || !password} className="rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90" data-testid="reauth-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
